import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import Reveal from "@/components/Reveal";

/**
 * "More designs in this collection" — sibling variants rail.
 * Renders nothing if the product has no variant_group or no siblings.
 *
 * Props:
 *   slug      — current product slug (used to fetch siblings)
 *   variant   — visual treatment: "default" (cream cards) | "themed" (translucent cards for themed PDPs)
 *   className — optional outer className override
 */
const DesignVariants = ({ slug, variant = "default", className = "" }) => {
  const [data, setData] = useState({ current_id: "", variants: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/products/${slug}/variants`)
      .then((r) => {
        if (!alive) return;
        setData(r.data || { current_id: "", variants: [] });
      })
      .catch(() => {
        if (alive) setData({ current_id: "", variants: [] });
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  // Hide entirely when no group or only the current product is in the group
  if (loading) return null;
  if (!data.variants || data.variants.length <= 1) return null;

  const isThemed = variant === "themed";

  const wrapperBg = isThemed
    ? "bg-white/5 border-white/15 backdrop-blur-md"
    : "bg-white border-border/40";
  const titleColor = isThemed ? "text-cream" : "text-charcoal";
  const subColor = isThemed ? "text-cream/70" : "text-mauve";
  const cardBase = isThemed
    ? "bg-white/10 border-white/15 hover:border-white/40"
    : "bg-cream border-border/40 hover:border-rose";
  const activeRing = isThemed
    ? "ring-2 ring-gold border-gold/60"
    : "ring-2 ring-rose border-rose/60";
  const nameColor = isThemed ? "text-cream" : "text-charcoal";
  const priceColor = isThemed ? "text-cream/80" : "text-mauve";

  return (
    <section
      data-testid="design-variants-section"
      className={`py-10 md:py-12 ${className}`}
    >
      <div className="rieave-container">
        <Reveal>
          <div className={`rounded-3xl border ${wrapperBg} p-6 md:p-8`}>
            <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
              <div>
                <p
                  className={`font-label text-[11px] uppercase tracking-[0.24em] ${subColor} mb-1.5`}
                >
                  Choose yours
                </p>
                <h2
                  className={`font-hero text-2xl md:text-[28px] leading-tight ${titleColor}`}
                >
                  More designs in this{" "}
                  <span className="serif-italic text-rose">collection</span>
                </h2>
              </div>
              <span
                className={`font-body text-sm ${subColor}`}
                data-testid="design-variants-count"
              >
                {data.variants.length} designs available
              </span>
            </div>

            <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-1 px-1">
              {data.variants.map((p, i) => {
                const isActive = p.id === data.current_id;
                const img = (p.images || [])[0] || "";
                const card = (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{
                      duration: 0.45,
                      delay: i * 0.06,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    whileHover={isActive ? {} : { y: -3 }}
                    className={`flex-shrink-0 w-[160px] md:w-[180px] rounded-2xl border overflow-hidden transition ${cardBase} ${
                      isActive ? activeRing : ""
                    }`}
                    data-testid={`variant-card-${p.slug}`}
                    data-active={isActive ? "true" : "false"}
                  >
                    <div className="relative aspect-square overflow-hidden bg-blush/30">
                      {img && (
                        <img
                          src={img}
                          alt={p.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                        />
                      )}
                      {isActive && (
                        <div
                          className="absolute top-2 right-2 inline-flex items-center gap-1 bg-rose text-cream text-[10px] font-label uppercase tracking-[0.18em] px-2 py-1 rounded-full shadow-sm"
                          data-testid="variant-current-badge"
                        >
                          <Check size={11} strokeWidth={3} /> Selected
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p
                        className={`font-cormorant text-base leading-snug line-clamp-2 ${nameColor}`}
                      >
                        {p.name}
                      </p>
                      <p
                        className={`font-body text-sm mt-1 ${priceColor}`}
                      >
                        {formatINR(p.price)}
                      </p>
                    </div>
                  </motion.div>
                );

                if (isActive) {
                  return (
                    <div key={p.id} className="cursor-default" aria-current="true">
                      {card}
                    </div>
                  );
                }
                return (
                  <Link
                    key={p.id}
                    to={`/product/${p.slug}`}
                    className="group block focus:outline-none focus:ring-2 focus:ring-rose rounded-2xl"
                    data-testid={`variant-link-${p.slug}`}
                  >
                    {card}
                  </Link>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default DesignVariants;
